import React from 'react';
import { Circle, Ellipse, G, Line, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** Solve duct size from required CFM and target velocity — both round + rect options. */
export function DiagramDuctSizeFromCFM() {
  // Inputs panel (top): CFM in, V target
  // Round option (left), Rect option (right)
  const ccx = 95, ccy = 135, R = 50;
  const rx = 195, ry = 95, rw = 110, rh = 80;
  const rcx = rx + rw / 2, rcy = ry + rh / 2;

  return (
    <DiagramCanvas>
      {/* Inputs banner */}
      <Rect x={20} y={28} width={310} height={32} fill="rgba(255,107,26,0.08)" stroke={DT.varColor} strokeWidth="0.8" rx="3" />
      <SvgText x={32} y={48} fill={DT.varColor} fontSize="10" fontFamily={DT.mono} fontWeight="700">CFM</SvgText>
      <SvgText x={62} y={48} fill={DT.desc} fontSize="9" fontFamily={DT.mono}>required</SvgText>
      <SvgText x={170} y={48} fill={DT.varColor} fontSize="10" fontFamily={DT.mono} fontWeight="700">V</SvgText>
      <SvgText x={185} y={48} fill={DT.desc} fontSize="9" fontFamily={DT.mono}>target velocity (FPM)</SvgText>

      {/* Step arrow */}
      <Line x1={175} y1={75} x2={175} y2={88} stroke={DT.leader} strokeWidth="1.2" />
      <Polygon points="175,90 171,84 179,84" fill={DT.leader} />
      <SvgText x={175} y={84} fill={DT.desc} fontSize="8" fontFamily={DT.mono} textAnchor="middle">A = Q / V</SvgText>

      {/* === Round option === */}
      <Ellipse cx={ccx} cy={ccy} rx={R} ry={R} fill={DT.fill} stroke={DT.stroke} strokeWidth="2" />
      {/* shading */}
      {[0.3, 0.55, 0.8].map((f, i) => (
        <Circle key={i} cx={ccx} cy={ccy} r={R * f} fill="none"
          stroke={DT.varColor} strokeWidth="0.5" opacity={0.3} />
      ))}
      {/* Diameter dimension */}
      <Line x1={ccx - R} y1={ccy} x2={ccx + R} y2={ccy} stroke={DT.dim} strokeWidth="1" strokeDasharray="3,3" />
      <Polygon points={`${ccx - R},${ccy} ${ccx - R + 6},${ccy - 3} ${ccx - R + 6},${ccy + 3}`} fill={DT.dim} />
      <Polygon points={`${ccx + R},${ccy} ${ccx + R - 6},${ccy - 3} ${ccx + R - 6},${ccy + 3}`} fill={DT.dim} />
      <SvgText x={ccx} y={ccy - 4} fill={DT.dim} fontSize="9" fontFamily={DT.mono} textAnchor="middle">D</SvgText>

      <SvgText x={ccx} y={ccy + R + 18} fill={DT.desc} fontSize="9" fontFamily={DT.mono} textAnchor="middle">Round option</SvgText>

      {/* === Rectangular option === */}
      <Rect x={rx} y={ry} width={rw} height={rh} fill={DT.fill} stroke={DT.stroke} strokeWidth="2" />
      {Array.from({ length: 6 }).map((_, i) => {
        const t = (i + 1) / 7;
        return (
          <Line key={i} x1={rx + 4} y1={ry + t * rh} x2={rx + rw - 4} y2={ry + t * rh}
            stroke={DT.varColor} strokeWidth="0.5" opacity={0.3} />
        );
      })}
      {/* W dim */}
      <Line x1={rx} y1={ry + rh + 14} x2={rx + rw} y2={ry + rh + 14} stroke={DT.dim} strokeWidth="1" />
      <Line x1={rx} y1={ry + rh + 10} x2={rx} y2={ry + rh + 18} stroke={DT.dim} strokeWidth="1" />
      <Line x1={rx + rw} y1={ry + rh + 10} x2={rx + rw} y2={ry + rh + 18} stroke={DT.dim} strokeWidth="1" />
      <SvgText x={rcx} y={ry + rh + 26} fill={DT.dim} fontSize="9" fontFamily={DT.mono} textAnchor="middle">W (known)</SvgText>
      {/* H dim */}
      <Line x1={rx + rw + 14} y1={ry} x2={rx + rw + 14} y2={ry + rh} stroke={DT.dim} strokeWidth="1" />
      <Line x1={rx + rw + 10} y1={ry} x2={rx + rw + 18} y2={ry} stroke={DT.dim} strokeWidth="1" />
      <Line x1={rx + rw + 10} y1={ry + rh} x2={rx + rw + 18} y2={ry + rh} stroke={DT.dim} strokeWidth="1" />
      <SvgText x={rx + rw + 22} y={rcy + 3} fill={DT.varColor} fontSize="10" fontFamily={DT.mono} fontWeight="700">H = ?</SvgText>

      <SvgText x={rcx} y={ry - 4} fill={DT.desc} fontSize="9" fontFamily={DT.mono} textAnchor="middle">Rectangular option</SvgText>

      {/* OR divider */}
      <SvgText x={172} y={140} fill={DT.desc} fontSize="11" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">OR</SvgText>

      {/* Solved labels */}
      <LeaderLine x1={ccx} y1={ccy + R} x2={50} y2={210} varName="D" desc="√(4A·144/π)" anchor="start" />
      <LeaderLine x1={rx + rw + 22} y1={rcy} x2={300} y2={210} varName="H" desc="A·144 / W" anchor="end" />

      <FormulaTag formula="A = Q/V    D = √(4A·144/π)    H = A·144 / W" />
    </DiagramCanvas>
  );
}
